import { useState } from "react";
import { PublicPlans } from "@/components/billing/Plans";
import { Navigate, useNavigate } from "react-router-dom";
import { Home as HomeIcon, Link2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { HouseQ, LegalFooter } from "@/components/brand/Brand";
import { Loader } from "@/components/common";
import { CreateHubForm } from "@/components/HubForms";

export default function Onboarding() {
  const { session, refresh, signOut } = useAuth();
  const nav = useNavigate();
  const [link, setLink] = useState("");
  const [linkError, setLinkError] = useState("");

  if (session === null) return <Loader />;
  if (!session) return <Navigate to="/signin" replace />;
  if (session.type === "kid") return <Navigate to={`/h/${session.last_organization_id}/home`} replace />;

  const join = (e) => {
    e.preventDefault();
    const m = link.trim().match(/invite\/([A-Za-z0-9]+)/) || link.trim().match(/^([A-Za-z0-9]{20,})$/);
    if (!m) return setLinkError("Paste the full invite link your family sent you");
    nav(`/invite/${m[1]}`);
  };

  return (
    <div className="flex min-h-screen flex-col bg-q-bg">
      <header className="flex items-center justify-between px-6 py-5">
        <div className="flex items-center gap-2.5"><HouseQ size={30} /><span className="font-display font-extrabold text-q-wordmark">The Queue</span></div>
        <button className="q-btn q-btn-ghost" onClick={async () => { await signOut(); nav("/signin"); }} data-testid="onboarding-signout-btn">Sign out</button>
      </header>
      <main className="mx-auto w-full max-w-4xl flex-1 px-6 py-10">
        <p className="q-eyebrow">Hi {session.user.name.split(" ")[0]}</p>
        <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight text-q-text sm:text-5xl" data-testid="onboarding-title">Create or join a hub</h1>
        <p className="mt-3 max-w-xl text-q-muted">A hub is your family's home base. Start a new one, or join one you've been invited to.</p>
        <div className="mt-10 grid gap-6 md:grid-cols-[1.2fr_1fr]">
          <section className="q-card q-rise" style={{ boxShadow: "0 0 40px rgba(var(--glow-home),0.08)" }}>
            <div className="mb-5 flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.18em] text-q-accent"><HomeIcon className="h-3.5 w-3.5" /> Start a new hub</div>
            <CreateHubForm onCreated={async (h) => { await refresh(); nav(`/h/${h.id}/home`, { replace: true }); }} />
          </section>
          <section className="q-card q-rise" style={{ animationDelay: "80ms" }}>
            <div className="mb-5 flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: "rgb(var(--glow-family))" }}><Link2 className="h-3.5 w-3.5" /> Join with an invite</div>
            <form onSubmit={join} className="space-y-4">
              <input className="q-input" placeholder="Paste your invite link" value={link} onChange={(e) => { setLink(e.target.value); setLinkError(""); }} data-testid="onboarding-invite-input" />
              {linkError && <p className="q-error" data-testid="onboarding-invite-error">{linkError}</p>}
              <button className="q-btn q-btn-secondary w-full" data-testid="onboarding-join-btn">Open invite</button>
            </form>
          </section>
        </div>
        <div className="mx-auto mt-10 w-full max-w-5xl" data-testid="onboarding-plans"><PublicPlans /></div>
      </main>
      <LegalFooter />
    </div>
  );
}
